import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Story {
  id: number;
  title: string;
  category: string;
  language: string;
  duration: string;
  rating: number;
  listeners: string;
  image: string;
  alt: string;
  trending: boolean;
}

interface TrendingStoriesProps {
  stories: Story[];
}

export default function TrendingStories({ stories }: TrendingStoriesProps) {
  return (
    <section className="py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-accent/10 px-4 py-2 rounded-full mb-4">
            <Icon name="FireIcon" size={20} variant="solid" className="text-accent" />
            <span className="text-sm font-semibold text-accent">Trending Now</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-text-primary mb-4">
            Stories Everyone's Talking About
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Discover the most popular audio stories across India, updated in real-time based on listener engagement
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {stories.map((story) => (
            <div
              key={story.id}
              className="group bg-card rounded-2xl overflow-hidden shadow-warm hover:shadow-warm-lg transition-all duration-300 hover:-translate-y-2"
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <AppImage
                  src={story.image}
                  alt={story.alt}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/50 to-transparent" />
                
                {story.trending && (
                  <div className="absolute top-4 right-4 bg-accent px-3 py-1 rounded-full flex items-center space-x-1">
                    <Icon name="FireIcon" size={14} variant="solid" className="text-accent-foreground" />
                    <span className="text-xs font-bold text-accent-foreground">Trending</span>
                  </div>
                )}

                <button className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center shadow-warm-lg hover:scale-110 transition-transform">
                    <Icon name="PlayIcon" size={28} variant="solid" className="text-primary-foreground ml-1" />
                  </div>
                </button>
              </div>

              <div className="p-5 space-y-3">
                <div>
                  <h3 className="font-heading font-bold text-lg text-text-primary mb-1 line-clamp-2 group-hover:text-primary transition-colors">
                    {story.title}
                  </h3>
                  <p className="text-sm text-text-secondary">
                    {story.category} • {story.language} • {story.duration}
                  </p>
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-border">
                  <div className="flex items-center space-x-1">
                    <Icon name="StarIcon" size={16} variant="solid" className="text-accent" />
                    <span className="text-sm font-semibold text-text-primary">{story.rating}</span>
                  </div>
                  <div className="flex items-center space-x-1 text-text-secondary">
                    <Icon name="UserGroupIcon" size={16} />
                    <span className="text-sm">{story.listeners}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <a
            href="/stories-library"
            className="inline-flex items-center space-x-2 px-8 py-4 bg-primary text-primary-foreground rounded-xl font-cta font-bold text-lg transition-all duration-300 hover:bg-primary-dark hover:shadow-warm-lg hover:scale-105"
          >
            <span>Explore All Stories</span>
            <Icon name="ArrowRightIcon" size={20} />
          </a>
        </div>
      </div>
    </section>
  );
}